const INDIAN_MOBILE_RE = /^[6-9]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const NAME_MAX_LENGTH = 50;
export const MIN_AGE_YEARS = 18;
const MAX_AGE_YEARS = 120;

const DEFAULT_FIELD_SCHEMA = { type: 'text', placeholder: 'Enter value' };

const FIELD_SCHEMA = {
  name: { label: 'Your Name', type: 'text', placeholder: 'Type your name' },
  gender: {
    label: 'Your Gender',
    type: 'select',
    placeholder: 'Select your gender',
    options: ['Male', 'Female', 'Other'],
  },
  email: {
    label: 'Your Email',
    type: 'email',
    placeholder: 'Type your email',
  },
  phone: {
    label: 'Your Phone Number',
    type: 'tel',
    placeholder: '10-digit mobile number',
  },
  dob: {
    label: 'Your Date of Birth',
    type: 'date',
    placeholder: 'YYYY-MM-DD',
  },
};

export function fieldSchema(fieldName) {
  return {
    label: fieldName,
    ...DEFAULT_FIELD_SCHEMA,
    ...FIELD_SCHEMA[fieldName],
  };
}

// Whole years elapsed since `isoDate` (YYYY-MM-DD), or null if unparseable.
// Compares month/day so someone turning 18 tomorrow is still 17 today.
export function ageInYears(isoDate) {
  const dob = new Date(isoDate);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDelta = today.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}

// Latest DOB that still satisfies the age floor, so a native date picker can
// grey out under-age dates instead of only failing on submit.
export function maxDobForAdult() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MIN_AGE_YEARS);
  return d.toISOString().split('T')[0];
}

// Null when the value is acceptable, otherwise the reason to show under the
// field. Keyed on schema.type, so validation follows the field's declared
// type rather than hardcoded field names.
export function validationError(schema, value) {
  if (!value) return null; // nothing typed yet — incomplete, not invalid
  switch (schema.type) {
    case 'tel':
      return INDIAN_MOBILE_RE.test(value)
        ? null
        : 'Enter a valid 10-digit Indian mobile number';
    case 'email':
      return EMAIL_RE.test(value.trim()) ? null : 'Enter a valid email address';
    case 'select':
      return schema.options?.includes(value)
        ? null
        : `Choose one of: ${(schema.options || []).join(', ')}`;
    case 'date': {
      const age = ageInYears(value);
      if (age === null) return 'Enter a valid date';
      if (age < 0) return 'Date of birth cannot be in the future';
      if (age < MIN_AGE_YEARS) return `You must be at least ${MIN_AGE_YEARS}`;
      if (age > MAX_AGE_YEARS) return 'Enter a valid date of birth';
      return null;
    }
    default:
      if (value.trim().length === 0) return null;
      return value.trim().length > NAME_MAX_LENGTH
        ? `Keep this under ${NAME_MAX_LENGTH} characters`
        : null;
  }
}

export function isFieldValid(schema, value) {
  if (!value || !value.trim()) return false;
  return validationError(schema, value) === null;
}
